import { useState } from "react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarDays, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

const iso = (d) => format(d, "yyyy-MM-dd");
const THIS_YEAR = new Date().getFullYear();
export const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

const DROPDOWN = {
  caption: "relative flex items-center justify-center px-10 pt-1",
  nav_button: "inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-transparent opacity-60 hover:opacity-100",
  nav_button_previous: "absolute left-0 z-20", nav_button_next: "absolute right-0 z-20",
  head_cell: "w-10 text-[0.8rem] font-normal text-muted-foreground",
  cell: "relative h-10 w-10 p-0 text-center text-sm",
  day: "h-10 w-10 rounded-full p-0 font-normal transition-colors hover:bg-secondary aria-selected:opacity-100",
  day_disabled: "text-muted-foreground opacity-30 pointer-events-none",
  caption_dropdowns: "flex items-center justify-center gap-2",
  dropdown_month: "relative inline-flex items-center", dropdown_year: "relative inline-flex items-center",
  dropdown: "absolute inset-0 z-10 w-full cursor-pointer opacity-0",
  caption_label: "flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-sm font-semibold capitalize",
  vhidden: "sr-only",
};

const Picker = ({ selected, onPick, fromYear, toYear, onClear, maxDate, minDate }) => (
  <div className="space-y-2">
    <Calendar mode="single" locale={fr} weekStartsOn={1} selected={selected} defaultMonth={selected} onSelect={(d) => d && onPick(d)}
      disabled={maxDate || minDate ? [maxDate && { after: maxDate }, minDate && { before: minDate }].filter(Boolean) : undefined}
      captionLayout="dropdown-buttons" fromYear={fromYear} toYear={toYear} classNames={DROPDOWN}
      components={{ IconDropdown: () => <ChevronDown className="h-3.5 w-3.5" /> }} className="mx-auto w-fit" />
    <div className="flex justify-between gap-2 px-3 pb-3">
      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => onPick(new Date())} data-testid="datepicker-today">Aujourd'hui</Button>
      {onClear && <Button type="button" variant="ghost" size="sm" className="rounded-full" onClick={onClear} data-testid="datepicker-clear">Effacer</Button>}
    </div>
  </div>
);

/** Drop-in for <Input type="date">: value "YYYY-MM-DD", onChange({ target: { value } }). Drawer on mobile, popover on desktop. */
export const DatePicker = ({ value, onChange, placeholder = "Choisir une date", required, testid, id, className, fromYear = THIS_YEAR - 30, toYear = THIS_YEAR + 10, noFuture, fromDate, toDate, ...rest }) => {
  const [open, setOpen] = useState(false);
  const mobile = useMediaQuery("(max-width: 640px)");
  const selected = value ? parseISO(value) : undefined;
  const emit = (v) => { onChange({ target: { value: v } }); setOpen(false); };
  const picker = <Picker selected={selected} fromYear={fromYear} toYear={noFuture ? THIS_YEAR : toYear} maxDate={noFuture ? new Date() : toDate || null} minDate={fromDate || null} onPick={(d) => emit(iso(d))} onClear={required ? null : () => emit("")} />;
  const trigger = (
    <button type="button" id={id} onClick={() => setOpen(true)} data-testid={testid || rest["data-testid"]}
      className={cn("flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}>
      <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className={cn("min-w-0 flex-1 truncate", !selected && "text-muted-foreground")}>{selected ? format(selected, mobile ? "d MMM yyyy" : "d MMMM yyyy", { locale: fr }) : placeholder}</span>
      {selected && !required && <X className="h-3.5 w-3.5 shrink-0 text-muted-foreground" onClick={(e) => { e.stopPropagation(); emit(""); }} data-testid="datepicker-inline-clear" />}
    </button>
  );
  return (
    <div className="relative">
      <input tabIndex={-1} aria-hidden required={required} value={value || ""} onChange={() => {}} className="pointer-events-none absolute bottom-0 left-1/2 h-px w-px opacity-0" />
      {mobile ? (
        <>
          {trigger}
          <Drawer open={open} onOpenChange={setOpen}>
            <DrawerContent data-testid="datepicker-drawer">
              <DrawerHeader><DrawerTitle className="font-heading">{placeholder}</DrawerTitle></DrawerHeader>
              {picker}
            </DrawerContent>
          </Drawer>
        </>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" data-testid="datepicker-popover">{picker}</PopoverContent>
        </Popover>
      )}
    </div>
  );
};

/** Drop-in for <Input type="month">: value "YYYY-MM". */
export const MonthPicker = ({ value, onChange, testid, years = 4 }) => {
  const [y, m] = (value || `${THIS_YEAR}-01`).split("-");
  const emit = (ny, nm) => onChange({ target: { value: `${ny}-${nm}` } });
  return (
    <div className="grid grid-cols-[1fr_6rem] gap-2" data-testid={testid}>
      <Select value={m} onValueChange={(nm) => emit(y, nm)}>
        <SelectTrigger className="capitalize" data-testid={`${testid}-month`}><SelectValue /></SelectTrigger>
        <SelectContent>{MONTHS_FR.map((l, i) => <SelectItem key={l} value={String(i + 1).padStart(2, "0")} className="capitalize">{l}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={y} onValueChange={(ny) => emit(ny, m)}>
        <SelectTrigger data-testid={`${testid}-year`}><SelectValue /></SelectTrigger>
        <SelectContent>{Array.from({ length: years }, (_, i) => String(THIS_YEAR - i)).map((yy) => <SelectItem key={yy} value={yy}>{yy}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
};
